
import React, { useEffect, useRef, useMemo } from 'react';
import { Node, Agent, NodeType, AgentType, Zone, ViewMode, SelectionRef } from '../../types';
import { NODE_COLORS, AGENT_COLORS, NODE_RADIUS, WAYPOINT_RADIUS } from '../../constants';
import { NODE_LABELS } from '../../utils/translations';
import { getZoneCenter } from '../../utils/geometry';

interface StaticLayerProps {
  nodes: Node[];
  zones: Zone[];
  width: number;
  height: number;
  pixelsPerMeter: number;
  selectedItems: SelectionRef[];
  hoveredZoneId: string | null;
  editMode: string;
  viewMode: ViewMode;
}

export const StaticLayer: React.FC<StaticLayerProps> = React.memo(({ 
  nodes, zones, width, height, pixelsPerMeter, selectedItems, hoveredZoneId, editMode, viewMode
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const selectedNodeIds = useMemo(() => new Set(selectedItems.filter(item => item.kind === 'NODE').map(item => item.id)), [selectedItems]);
  const selectedZoneIds = useMemo(() => new Set(selectedItems.filter(item => item.kind === 'ZONE').map(item => item.id)), [selectedItems]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    zones.forEach(z => {
      const isHovered = hoveredZoneId === z.id;
      const isSelected = selectedZoneIds.has(z.id);
      const baseColor = NODE_COLORS[z.type] || '#475569';
      
      ctx.fillStyle = isHovered || isSelected ? `${baseColor}66` : `${baseColor}33`;
      ctx.strokeStyle = isSelected ? '#06b6d4' : (isHovered ? '#ffffff' : baseColor);
      ctx.setLineDash(isSelected ? [] : [5, 5]);
      ctx.lineWidth = isSelected ? 3 : (isHovered ? 2 : 1);
      
      const rects = z.rects || [];
      
      // Piešiame visus sudėtinės zonos stačiakampius
      rects.forEach(r => {
        ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.strokeRect(r.x, r.y, r.w, r.h);
      });
      ctx.setLineDash([]);

      // Piešiame vieną etiketę zonos geometriniame centre
      if (rects.length > 0) {
        const center = getZoneCenter(z);
        const label = (z.label || NODE_LABELS[z.type] || String(z.type)).toUpperCase();
        
        ctx.font = isSelected ? 'bold 12px JetBrains Mono, monospace' : 'bold 10px JetBrains Mono, monospace';
        const textWidth = ctx.measureText(label).width;
        
        ctx.fillStyle = isSelected ? 'rgba(6, 182, 212, 0.95)' : 'rgba(15, 23, 42, 0.9)';
        ctx.fillRect(center.x - textWidth / 2 - 4, center.y - 10, textWidth + 8, 20);
        
        ctx.fillStyle = isHovered || isSelected ? '#ffffff' : '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, center.x, center.y);
      }
    });

    nodes.forEach(node => {
      const isWaypoint = node.type === NodeType.WAYPOINT;
      const isSelected = selectedNodeIds.has(node.id);
      if (isWaypoint && editMode === 'SELECT' && !isSelected) return;
      const radius = isWaypoint ? WAYPOINT_RADIUS + 2 : NODE_RADIUS;
      let color = NODE_COLORS[node.type] || '#94a3b8';
      if (viewMode === 'CONTAMINATION') {
        const level = Math.max(0, Math.min(100, node.contamination)) / 100;
        color = `rgb(${Math.round(34 + level * 205)}, ${Math.round(197 - level * 129)}, ${Math.round(94 - level * 26)})`;
      } else if (viewMode === 'HEATMAP') {
        const occupancy = node.capacity > 0 ? Math.min(1, node.occupants.length / node.capacity) : 0;
        color = `rgb(${Math.round(34 + occupancy * 205)}, ${Math.round(197 - occupancy * 129)}, 68)`;
      }
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? '#ffffff' : color;
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#06b6d4' : 'rgba(255,255,255,0.8)';
      ctx.lineWidth = isSelected ? 3 : 1.5;
      ctx.stroke();
    });
  }, [nodes, zones, width, height, selectedNodeIds, selectedZoneIds, hoveredZoneId, editMode, viewMode]);

  return (
    <canvas 
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none z-10"
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
});

export const AgentLayer: React.FC<{ 
  agents: Agent[]; 
  selectedAgentId?: string | null; 
  width: number;
  height: number;
}> = React.memo(({ agents, selectedAgentId, width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    agents.forEach((agent) => {
      const isSelected = selectedAgentId === agent.id;
      const color = AGENT_COLORS[agent.type] || '#cbd5e1';
      const radius = agent.type === AgentType.DOCTOR || agent.type === AgentType.NURSE ? 7 : 5.5;

      if (agent.infected) {
        ctx.save();
        ctx.shadowBlur = 12;
        ctx.shadowColor = '#84cc16';
        ctx.fillStyle = 'rgba(132, 204, 22, 0.4)';
        ctx.beginPath();
        ctx.arc(agent.x, agent.y, radius + 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      ctx.beginPath();
      ctx.arc(agent.x, agent.y, isSelected ? radius + 3 : radius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#ffffff' : 'rgba(0,0,0,0.3)';
      ctx.lineWidth = isSelected ? 3 : 1;
      ctx.stroke();
    });
  }, [agents, selectedAgentId, width, height]);

  return (
    <canvas 
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none z-30"
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
});
